import { useEffect, useState, useRef } from 'react';
import { useZxing } from 'react-zxing';
import { useFlow } from '../hooks/useFlow';
import { useReceptionInfo } from '../hooks/useReceptionInfo';
import Layout from './Layout';
import { getPatient, listFacilityPatientsByFacilityIdAndKarteNo } from '@/graphql/queries';
import { GetPatientQuery, ListFacilityPatientsByFacilityIdAndKarteNoQuery } from '@/API';
import { createReception } from '@/graphql/mutations';
import { getCurrentDateTime } from '../logic/getCurrentDateTimeMs';
import { useErrorText } from '../hooks/useErrorText';
import { ERROR_TEXT } from '../const/errorText';
import { useSearchParams } from 'react-router-dom';
import qrScanGuidImage from '../../../assets/QR_scan.svg';
import { ClinicProps } from '../types/Props';
import * as Sentry from '@sentry/react';
import { get } from '@/lib/axiosClient';
import { executeQuery } from '@/lib/glaphqlClient';
import { setUser } from '@/lib/sentryClient';
import { executePost } from '@/lib/amplifyRestClient';
import { Backdrop, CircularProgress } from '@mui/material';

interface QrCodeReaderProps extends ClinicProps {
  facilityId: string | null;
}

// MEMO 0: 未受付、 1: 受付済み
type Receptionkbn = '0' | '1';

type ReservationInfo = {
  outpatientId: string;
  outpatientName: string;
  date: string;
  startTime: string;
  receptionKbn: string;
};

function QrCodeReader({ clinicName, facilityId }: QrCodeReaderProps) {
  const { setFlow } = useFlow();
  const { setReceptionNumber, setReceptionName } = useReceptionInfo();
  const { setErrorText } = useErrorText();
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);
  const [QRdata, setQRData] = useState<string | undefined>(undefined);
  const [cognitoId, setCognitoId] = useState<string | undefined>(undefined);
  const [patientName, setPatientName] = useState<string>('');
  const [reservationInfo, setReservationInfo] = useState<ReservationInfo | undefined>(undefined);
  const { year, month, day, hours, minutes, seconds } = getCurrentDateTime();
  const today = `${year}${month}${day}`;
  const [searchParams] = useSearchParams();
  const cameraMode = searchParams.get('cameraMode');
  const [isLoading, setLoading] = useState(false); // QRコードリーダーでの読み取りが連続で行われないためのフラグ

  useEffect(() => {
    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const videoDevices = devices.filter((device) => device.kind === 'videoinput');

      // インカメラを見つける
      const frontCamera =
        videoDevices.find((device) => device.label.toLowerCase().includes('front')) || videoDevices[0];

      // インカメラのデバイスIDを設定
      if (frontCamera) {
        setDeviceId(frontCamera.deviceId);
      }
    };

    getDevices();
  }, []);

  const { ref } = useZxing({
    deviceId: deviceId,
    onDecodeResult(result) {
      setQRData(result.getText());
    },
    onError(error) {
      setErrorText(ERROR_TEXT.qrCodeReadingFailure);
      Sentry.captureException(ERROR_TEXT.qrCodeReadingFailure);
      setFlow('error');
    }
  });

  // カルテNoからPatient情報を取得
  const fetchPatientByKarteNoAndFacilityId = async (karteNo: string, facilityId: string): Promise<any> => {
    const fetchAllPages = async (nextToken: string | null = null): Promise<any> => {
      Sentry.addBreadcrumb({
        level: 'info',
        message: 'fetchAllPages実行',
        data: {
          karteNo,
          facilityId,
          nextToken
        }
      });

      const filter = {
        karteNo: karteNo,
        facilityId: {
          eq: facilityId
        }
      };
      const response = await executeQuery<ListFacilityPatientsByFacilityIdAndKarteNoQuery>(
        listFacilityPatientsByFacilityIdAndKarteNo,
        { ...filter, nextToken: nextToken },
        'iam'
      );
      if (!response.result) {
        return null;
      }

      const lacilityPatients = response.data?.listFacilityPatientsByFacilityIdAndKarteNo?.items ?? [];
      const newNextToken = response.data?.listFacilityPatientsByFacilityIdAndKarteNo?.nextToken;

      // itemsがない且つnextTokenがない場合は処理を終了
      if (lacilityPatients.length === 0 && !newNextToken) {
        Sentry.captureException(`listFacilityPatientsは0件でした。`);
        setErrorText(`${ERROR_TEXT.qrCodeReadingFailure} (data not found)`);
        return;
      }

      // データが見つかったらその時点で返す
      if (lacilityPatients.length > 0) {
        return lacilityPatients[0];
      }

      // 次のページがある場合は再帰的に呼び出し
      if (newNextToken) {
        return await fetchAllPages(newNextToken);
      } else {
        return null;
      }
    };

    return fetchAllPages();
  };

  // 読み取ったQRのデータを元にcognitoIdを取得する
  useEffect(() => {
    (async () => {
      if (QRdata != null) {
        Sentry.addBreadcrumb({
          level: 'info',
          message: 'QRコード文字列',
          data: {
            qrData: QRdata
          }
        });

        // QR読み込みが連続で行われないようloading状態にする
        // loading状態の解除はQR読み取り処理の過程で失敗したタイミング、もしくは受付ステータスの更新と受付情報をDynamoに登録したタイミングで行う
        setLoading(true);
        let unverifiedCognitoId: string | undefined = undefined;
        let verifiedCognitoId: string | undefined = undefined;

        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(QRdata)) {
          // QRdataがUUIDの場合（CognitoIdが含まれている場合の処理）
          unverifiedCognitoId = QRdata;
        } else {
          // それ以外はカルテNo.の処理
          if (facilityId) {
            const facilityPatient: any | null = await fetchPatientByKarteNoAndFacilityId(QRdata, facilityId);
            if (facilityPatient) {
              Sentry.addBreadcrumb({
                level: 'info',
                message: 'fetchPatientByKarteNoAndFacilityId 結果',
                data: {
                  facilityPatient
                }
              });

              unverifiedCognitoId = facilityPatient.patientId;
            } else {
              setErrorText(`${ERROR_TEXT.qrCodeReadingFailure} (patient not found)`);
              setFlow('error');
              setLoading(false);
              return;
            }
          }
        }

        if (unverifiedCognitoId) {
          const response = await executeQuery<GetPatientQuery>(getPatient, { id: unverifiedCognitoId }, 'iam');
          if (!response.result) {
            setErrorText(`${ERROR_TEXT.qrCodeReadingFailure} (user id is not found)`);
            setFlow('error');
            setLoading(false);
            return;
          }

          const patient = response?.data?.getPatient;
          if (patient != null) {
            const fullName = `${patient.familyName} ${patient.firstName}`;
            setPatientName(fullName);
            verifiedCognitoId = patient.id;
            setUser(verifiedCognitoId, fullName);
          }
        } else {
          Sentry.captureException('set CognitoId Failure');
          setErrorText(`${ERROR_TEXT.qrCodeReadingFailure} (user id is not found)`);
          Sentry.captureException(`${ERROR_TEXT.qrCodeReadingFailure} (user id is not found)`);
          setFlow('error');
          setLoading(false);
          return;
        }

        if (verifiedCognitoId != null) {
          setCognitoId(verifiedCognitoId);
        } else {
          // graphQLのfetchには成功したがcognitoIDが取得できなかった
          Sentry.captureException('set CognitoId Failure');
          setErrorText(`${ERROR_TEXT.qrCodeReadingFailure} (user id is not found)`);
          Sentry.captureException(`${ERROR_TEXT.qrCodeReadingFailure} (user id is not found)`);
          setFlow('error');
          setLoading(false);
          return;
        }
      }
    })();
  }, [QRdata]);

  // 本日の予約情報一覧を取得する
  const getReservation = async (cognitoId: string): Promise<ReservationInfo | undefined> => {
    Sentry.addBreadcrumb({
      level: 'info',
      message: 'getReservation 実行',
      data: {
        cognitoId
      }
    });

    const response = await get(`api/reservation/getreservationlist/${cognitoId}/${today}`);
    if (!response.result) {
      setErrorText(`${ERROR_TEXT.receptionFailure} (reservation data is not found)`);
      setFlow('error');
      return undefined;
    }

    Sentry.addBreadcrumb({
      level: 'info',
      message: 'getReservation 結果',
      data: {
        reservationList: response.data
      }
    });

    const filteredList: any[] = response.data.filter((item: ReservationInfo) => item.receptionKbn === '0');
    if (filteredList.length === 0) {
      Sentry.captureException(
        `本日の予約が見つかりませんでした。receptionStatusList response: ${JSON.stringify(response.data)}`
      );
      setErrorText(`${ERROR_TEXT.receptionFailure} (reservation data is not found)`);
      setFlow('error');
      return;
    }
    filteredList.sort((a: ReservationInfo, b: ReservationInfo) => parseInt(a.startTime) - parseInt(b.startTime));
    const targetReservation = filteredList[0];
    return targetReservation;
  };

  // cognitoIdをもとに予約情報を取得
  useEffect(() => {
    (async () => {
      if (cognitoId != null) {
        // 予約取得API
        const reservation = await getReservation(cognitoId);
        if (reservation != null) {
          setReservationInfo(reservation);
        } else {
          setErrorText(`${ERROR_TEXT.receptionFailure} (reservation data is not found)`);
          Sentry.captureException(`${ERROR_TEXT.qrCodeReadingFailure} (reservation data is not found)`);
          setFlow('error');
          setLoading(false);
          return;
        }
      }
    })();
  }, [cognitoId]);

  // 受付ステータスの更新
  // FIXME LineIdのパラメータが不足している
  const updateReservationStatus = async (reservationInfo: ReservationInfo) => {
    Sentry.addBreadcrumb({
      level: 'info',
      message: 'updateReservationStatus 実行',
      data: {
        reservationInfo
      }
    });

    const response = await executePost({
      apiName: 'medirecoWebApi',
      path: '/status-update-accepted',
      options: {
        body: {
          // FIXME facilityIdを仕様に合わせて修正
          facilityId: facilityId,
          cognitoId: cognitoId ? cognitoId : null,
          date: today,
          outpatientId: reservationInfo.outpatientId,
          startTime: reservationInfo.startTime,
          receptionDateTime: `${today}${hours}${minutes}${seconds}`
        }
      }
    });

    if (!response.result) {
      setErrorText(`${ERROR_TEXT.receptionFailure} (update reception status failure)`);
      setFlow('error');
      return;
    }

    Sentry.addBreadcrumb({
      level: 'info',
      message: 'updateReservationStatus 完了'
    });

    return response.data;
  };

  // 受付ステータスの更新と受付情報をDynamoに登録
  useEffect(() => {
    (async () => {
      if (reservationInfo != null) {
        const res = await updateReservationStatus(reservationInfo);
        if (res != null && cognitoId != null) {
          // Dynamoに受付情報を登録して受付完了画面を表示
          const response = await executeQuery(
            createReception,
            {
              input: {
                patientId: cognitoId,
                date: reservationInfo.date,
                outpatientId: reservationInfo.outpatientId,
                outpatientName: reservationInfo.outpatientName,
                startTime: reservationInfo.startTime
              }
            },
            'iam'
          );
          if (!response.result) {
            setErrorText(`${ERROR_TEXT.receptionFailure} (save reception data failure)`);
            setFlow('error');
            setLoading(false);
            return;
          }

          setReceptionNumber(reservationInfo.outpatientId);
          setReceptionName(patientName);
          setFlow('finished');
          setLoading(false);
        }
      }
    })();
  }, [reservationInfo, cognitoId]);

  const [inputData, setInputData] = useState<string>('');
  const [isInputComplete, setInputComplete] = useState<boolean>(false);
  const [isImeInput, setImeInput] = useState<boolean>(false);

  const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    // MEMO: 日本語カナ入力時は下記の値がinputTypeにセットされる
    const imeTypes = ['insertCompositionText', 'deleteCompositionText', 'insertFromComposition', 'deleteByComposition'];
    if (imeTypes.includes(nativeEvent.inputType)) {
      setImeInput(true);
    } else {
      setImeInput(false);
    }
    setInputData(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Enterキーが押されたかどうかをチェック
    if (event.key === 'Enter') {
      event.preventDefault(); // リロードを防ぐ
      if (event.nativeEvent.isComposing || event.keyCode === 229) {
        // MEMO: isComposingはsafari以外では正しく動作しないため、deprecatedのkeyCodeでも判定を入れている
        setImeInput(true);
      } else {
        // 半角英数字入力の場合のみ正しいQRデータとして登録
        setInputComplete(true);
      }
    }
  };

  // 日本語カナ入力の場合はalertを表示する
  useEffect(() => {
    if (isImeInput) {
      setTimeout(() => {
        alert('全角カナ入力になっています。係員をお呼びください');
        setFlow('error');
      }, 1500);
    }
  }, [isImeInput]);

  useEffect(() => {
    if (isInputComplete) {
      setQRData(inputData);
    }
  }, [isInputComplete]);

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleFocusOut = (event: FocusEvent) => {
      // フォーム内にアクティブ要素がない場合は最初の要素にフォーカス
      setTimeout(() => {
        if (formRef.current && !formRef.current.contains(document.activeElement)) {
          inputRef.current?.focus();
        }
      }, 0);
    };

    const formElement = formRef.current;
    if (formElement) {
      formElement.addEventListener('focusout', handleFocusOut);
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener('focusout', handleFocusOut);
      }
    };
  }, []);

  return (
    <Layout flowName="QR読み取り" clinicName={clinicName || undefined}>
      {cameraMode === 'true' ? (
        <>
          <p className="text-2xl sm:text-3xl font-bold text-gray-500 my-14 text-center">
            QRコードをカメラ(上部)にかざしてください。
          </p>
          <video
            ref={ref}
            className="w-full aspect-video object-cover rounded-2xl"
            style={{ transform: 'scaleX(-1)' }}
          />
        </>
      ) : (
        <>
          <p className="text-2xl sm:text-2xl font-bold text-gray-500 my-12 text-center">
            右側のQRコードリーダーにお手持ちのコードをかざしてください。
          </p>
          <div>
            <img src={qrScanGuidImage} alt="" />
          </div>
          <form ref={formRef}>
            <input
              type="text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onChangeText(e);
              }}
              autoFocus
              onKeyDown={(e) => handleKeyPress(e)}
              style={{ opacity: 0, position: 'absolute', left: '-9999px' }}
              disabled={isLoading}
              ref={inputRef}
            />
          </form>
        </>
      )}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={isLoading}>
        <CircularProgress color={'inherit'} size="4rem" thickness={6} />
      </Backdrop>
    </Layout>
  );
}

export default QrCodeReader;
